/** Repeating a save while the server waits for the network. Free of browser imports so it can be checked alone. */

export type Pending = { pending: string; retry_after?: number };
export type Attempt<T> = { done: T } | { waiting: Pending };

export const SAVE_TIMEOUT_MS = 5 * 60_000;
export const SAVING_WAIT = "Saving the subscription…\nWaiting for the network to confirm. This can take a minute.";
export const STILL_WAITING =
  "Still waiting for the network. Your wallet steps are done; try again in a minute.";

/**
 * Send the same save until the server stops answering 202. The server keeps each request short and
 * the save is safe to repeat, so a slow approve or register never surfaces as an error before 5 minutes.
 */
export async function untilSaved<T>(
  attempt: () => Promise<Attempt<T>>,
  opts: {
    onWaiting?: (step: string) => void;
    timeoutMs?: number;
    sleep?: (ms: number) => Promise<void>;
    now?: () => number;
  } = {}
): Promise<T> {
  const sleep = opts.sleep || ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const now = opts.now || (() => Date.now());
  const deadline = now() + (opts.timeoutMs ?? SAVE_TIMEOUT_MS);
  for (;;) {
    const result = await attempt();
    if ("done" in result) return result.done;
    opts.onWaiting?.(result.waiting.pending);
    const pause = Math.min(Math.max(Number(result.waiting.retry_after) || 5, 2), 15) * 1000;
    if (now() + pause > deadline) throw new Error(STILL_WAITING);
    await sleep(pause);
  }
}
